import React, { useState, useEffect } from 'react';
import {
  X,
  Users,
  Lock,
  Globe,
  Check,
  Sparkles,
  Crown,
  Search,
  Code2,
  Cpu,
  Palette,
  Megaphone,
  ShieldCheck,
  Briefcase,
  Layers,
  AtSign,
} from 'lucide-react';
import { useTenant, Space } from '../../context/TenantContext.tsx';
import { useEscapeKey } from '../../hooks/useEscapeKey.ts';
import { useAuth } from '../../context/AuthContext.tsx';

interface CreateSpaceModalProps {
  isOpen: boolean;
  onClose: () => void;
  editingSpace?: Space | null;
  onSuccess?: (space: Space) => void;
}

const SPACE_COLORS = [
  '#026fc1', // IHB Primary Blue
  '#6366f1', // Indigo
  '#8b5cf6', // Violet
  '#ec4899', // Pink
  '#ef4444', // Red
  '#f59e0b', // Amber
  '#10b981', // Emerald
  '#06b6d4', // Cyan
  '#00203a', // Deep Navy
];

const TEAM_ICONS = [
  { id: 'code', label: 'Engineering', Icon: Code2 },
  { id: 'palette', label: 'Design', Icon: Palette },
  { id: 'cpu', label: 'Platform / AI', Icon: Cpu },
  { id: 'megaphone', label: 'Marketing', Icon: Megaphone },
  { id: 'shield', label: 'QA / Security', Icon: ShieldCheck },
  { id: 'briefcase', label: 'Operations', Icon: Briefcase },
  { id: 'layers', label: 'Product', Icon: Layers },
  { id: 'users', label: 'General Team', Icon: Users },
];

export const CreateSpaceModal: React.FC<CreateSpaceModalProps> = ({
  isOpen,
  onClose,
  editingSpace = null,
  onSuccess,
}) => {
  const { createSpace, updateSpace, currentTenant, reloadSpaces } = useTenant();
  const { user, token } = useAuth();

  const isEditMode = Boolean(editingSpace);
  const isAdmin = user?.role === 'admin';

  const [name, setName] = useState('');
  const [handle, setHandle] = useState('');
  const [handleTouched, setHandleTouched] = useState(false);
  const [description, setDescription] = useState('');
  const [color, setColor] = useState(SPACE_COLORS[0]);
  const [icon, setIcon] = useState('code');
  const [isPrivate, setIsPrivate] = useState(false);

  const [leadUserIds, setLeadUserIds] = useState<string[]>([]);
  const [memberUserIds, setMemberUserIds] = useState<string[]>([]);

  const [workspaceUsers, setWorkspaceUsers] = useState<any[]>([]);
  const [userSearch, setUserSearch] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);
  useEscapeKey(isOpen, onClose);
  const [error, setError] = useState<string | null>(null);
  // Live check of the @handle: null = not checked yet / checking
  const [handleCheck, setHandleCheck] = useState<{ available: boolean; message: string } | null>(null);

  useEffect(() => {
    setHandleCheck(null);
    const clean = handle.replace(/^@+/, '').trim();
    if (!isOpen || !token || !clean) return;
    if (editingSpace && clean === (editingSpace.slug || '').toLowerCase()) return;
    const timer = setTimeout(async () => {
      try {
        const qs = new URLSearchParams({ handle: clean, exceptId: editingSpace?.id || '' });
        const res = await fetch(`/api/spaces/handle-available?${qs}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
            ...(currentTenant?.id ? { 'x-tenant-id': currentTenant.id } : {}),
          },
        });
        if (res.ok) {
          const d = await res.json();
          setHandleCheck({ available: Boolean(d.available), message: d.message || '' });
        }
      } catch {
        // the server still checks when the team is saved
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [handle, isOpen, token, user?.id, editingSpace]);

  // Load workspace users
  useEffect(() => {
    if (!isOpen || !token) return;

    const loadDirectoryAndBoards = async () => {
      try {
        const usersRes = await fetch('/api/users', {
          headers: {
            Authorization: `Bearer ${token}`,
            ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
          },
        });

        if (usersRes.ok) {
          const uData = await usersRes.json();
          setWorkspaceUsers(
            (uData.users || []).filter((u: any) => u.role !== 'guest' && u.role !== 'viewer')
          );
        }
      } catch (err) {
        console.warn('Failed to load directory for team modal:', err);
      }
    };

    loadDirectoryAndBoards();
  }, [isOpen, token, user?.id]);

  // Populate form when opening in Create or Edit mode
  useEffect(() => {
    if (!isOpen) return;
    setError(null);

    if (editingSpace) {
      setName(editingSpace.name || '');
      setHandle(
        (editingSpace.slug || editingSpace.name || '')
          .toLowerCase()
          .replace(/^@+/, '')
      );
      setHandleTouched(true);
      setDescription(editingSpace.description || '');
      setColor(editingSpace.color || SPACE_COLORS[0]);
      setIcon(editingSpace.icon || 'users');
      setIsPrivate(Boolean(editingSpace.isPrivate));

      const existingLeads =
        editingSpace.leads?.map((l) => l.id) ||
        editingSpace.members?.filter((m) => m.spaceRole === 'lead').map((m) => m.id) ||
        (user?.id ? [user.id] : []);
      const existingMembers =
        editingSpace.members?.map((m) => m.id) || [];

      setLeadUserIds(existingLeads.length > 0 ? existingLeads : user?.id ? [user.id] : []);
      setMemberUserIds(existingMembers);
    } else {
      setName('');
      setHandle('');
      setHandleTouched(false);
      setDescription('');
      setColor(SPACE_COLORS[0]);
      setIcon('code');
      setIsPrivate(false);
      setLeadUserIds(user?.id ? [user.id] : []);
      setMemberUserIds(user?.id ? [user.id] : []);
    }
  }, [isOpen, editingSpace, user?.id]);

  if (!isOpen) return null;

  const handleNameChange = (val: string) => {
    setName(val);
    if (!handleTouched) {
      const slug = val
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
      setHandle(slug);
    }
  };

  const toggleLead = (uid: string) => {
    if (!isAdmin) return; // Strictly Admin only
    setLeadUserIds((prev) => {
      const next = prev.includes(uid) ? prev.filter((id) => id !== uid) : [...prev, uid];
      return next;
    });
    if (!memberUserIds.includes(uid)) {
      setMemberUserIds((prev) => [...prev, uid]);
    }
  };

  const toggleMember = (uid: string) => {
    setMemberUserIds((prev) =>
      prev.includes(uid) ? prev.filter((id) => id !== uid) : [...prev, uid]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Team Name is required.');
      return;
    }
    if (leadUserIds.length === 0) {
      setError('At least one Team Lead must be assigned.');
      return;
    }
    if (handleCheck && !handleCheck.available) {
      setError(handleCheck.message);
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      const cleanSlug =
        handle
          .trim()
          .replace(/^@+/, '')
          .toLowerCase()
          .replace(/[^a-z0-9-_]+/g, '-')
          .replace(/^-|-$/g, '') ||
        name
          .trim()
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-');

      const payload = {
        name: name.trim(),
        slug: cleanSlug,
        handle: `@${cleanSlug}`,
        description: description.trim(),
        color,
        icon,
        isPrivate,
        leadUserIds,
        memberUserIds: Array.from(new Set([...memberUserIds, ...leadUserIds])),
      };

      let saved: Space;
      if (isEditMode && editingSpace) {
        saved = await updateSpace(editingSpace.id, payload);
      } else {
        saved = await createSpace(payload);
      }

      await reloadSpaces();
      if (onSuccess) onSuccess(saved);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save team');
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredUsers = workspaceUsers.filter((u) => {
    const q = userSearch.toLowerCase();
    return (
      (u.fullName || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      (u.title || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-xs p-4 overflow-y-auto">
      <div className="bg-white dark:bg-surface-900 border border-surface-200 dark:border-surface-800 rounded-3xl w-full max-w-2xl shadow-modal overflow-hidden my-auto animate-in fade-in zoom-in-95 duration-200 max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-200 dark:border-surface-800 bg-gradient-to-r from-[#0162fc]/10 via-[#4b2efa]/5 to-transparent shrink-0">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center text-white shadow-2xs"
              style={{ backgroundColor: color }}
            >
              <Users className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base font-bold text-surface-900 dark:text-surface-100">
                {isEditMode ? `Edit Team: ${editingSpace?.name}` : 'Create New Team'}
              </h2>
              <p className="text-xs text-surface-500 dark:text-surface-400">
                {isEditMode
                  ? 'Update team identity, @handle and members'
                  : `Create a team with its own #team-channel in ${currentTenant?.name || 'Workspace'}`}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-surface-400 hover:text-surface-700 dark:hover:text-surface-200 hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable Form Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5 overflow-y-auto flex-1">
          {error && (
            <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-500 text-xs font-semibold">
              {error}
            </div>
          )}

          {/* 1. Team Name & 2. Team Handle (@slug) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-surface-500 mb-1.5">
                1. Team Name <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                required
                placeholder="e.g., Frontend, Design, DevOps"
                value={name}
                onChange={(e) => handleNameChange(e.target.value)}
                className="w-full px-3.5 py-2.5 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-sm text-surface-900 dark:text-surface-100 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-surface-500 mb-1.5">
                2. Team Handle (@slug) <span className="text-rose-500">*</span>
              </label>
              <div className="relative">
                <AtSign className="w-4 h-4 text-brand-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  required
                  placeholder="frontend"
                  value={handle.replace(/^@+/, '')}
                  onChange={(e) => {
                    setHandleTouched(true);
                    setHandle(
                      e.target.value
                        .toLowerCase()
                        .replace(/^@+/, '')
                        .replace(/[^a-z0-9-_]/g, '-')
                    );
                  }}
                  className={`w-full pl-9 pr-3.5 py-2.5 rounded-xl bg-surface-50 dark:bg-surface-800 border text-sm font-mono text-surface-900 dark:text-surface-100 focus:outline-none focus:ring-1 ${
                    handleCheck && !handleCheck.available
                      ? 'border-rose-400 focus:ring-rose-500'
                      : 'border-surface-200 dark:border-surface-700 focus:ring-brand-500'
                  }`}
                />
              </div>
              {handleCheck && !handleCheck.available ? (
                <p className="text-[10px] font-semibold text-rose-500 mt-1">{handleCheck.message}</p>
              ) : handleCheck?.available ? (
                <p className="text-[10px] font-semibold text-emerald-600 mt-1">@{handle.replace(/^@+/, '')} is available</p>
              ) : (
                <p className="text-[10px] text-surface-500 mt-1">
                  Auto-provisions <span className="font-mono font-bold text-brand-600 dark:text-brand-400">#{handle || 'team'}</span> group chat in Messages
                </p>
              )}
            </div>
          </div>

          {/* 3. Description */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-surface-500 mb-1.5">
              3. Description
            </label>
            <textarea
              rows={2}
              placeholder="e.g., Tasks, sprints, and code reviews for the frontend developers"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3.5 py-2 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-sm text-surface-900 dark:text-surface-100 focus:outline-none focus:ring-1 focus:ring-brand-500 resize-none"
            />
          </div>

          {/* 4. Icon & Theme Color */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-surface-500 mb-2">
                4a. Team Icon
              </label>
              <div className="grid grid-cols-4 gap-1.5">
                {TEAM_ICONS.map(({ id, label, Icon }) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setIcon(id)}
                    className={`flex flex-col items-center justify-center p-2 rounded-xl border text-[10px] font-semibold transition-all cursor-pointer ${
                      icon === id
                        ? 'border-brand-500 bg-brand-50 text-brand-600 dark:bg-brand-950/60 dark:text-brand-300'
                        : 'border-surface-200 dark:border-surface-700 bg-surface-50 dark:bg-surface-800 text-surface-600 dark:text-surface-400 hover:text-surface-900 dark:hover:text-surface-100'
                    }`}
                    title={label}
                  >
                    <Icon className="w-4 h-4 mb-0.5" />
                    <span className="truncate w-full text-center">{label.split(' ')[0]}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-surface-500 mb-2">
                4b. Theme Accent Color
              </label>
              <div className="flex items-center gap-2 flex-wrap pt-1">
                {SPACE_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setColor(c)}
                    className="w-8 h-8 rounded-xl flex items-center justify-center transition-transform hover:scale-110 shadow-2xs cursor-pointer"
                    style={{ backgroundColor: c }}
                  >
                    {color === c && <Check className="w-4 h-4 text-white" />}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* 5. Visibility (Open vs Private) */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-surface-500 mb-2">
              5. Team Visibility
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setIsPrivate(false)}
                className={`flex items-center gap-2.5 p-3 rounded-xl border text-left transition-all cursor-pointer ${
                  !isPrivate
                    ? 'border-brand-500 bg-brand-50/60 dark:bg-brand-950/40 text-brand-900 dark:text-brand-200'
                    : 'border-surface-200 dark:border-surface-700 bg-surface-50 dark:bg-surface-800 text-surface-600 dark:text-surface-400'
                }`}
              >
                <Globe className={`w-4 h-4 shrink-0 ${!isPrivate ? 'text-brand-500' : ''}`} />
                <div>
                  <div className="text-xs font-bold">Open Team</div>
                  <div className="text-[10px] opacity-80">
                    Visible to workspace with self-service join
                  </div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setIsPrivate(true)}
                className={`flex items-center gap-2.5 p-3 rounded-xl border text-left transition-all cursor-pointer ${
                  isPrivate
                    ? 'border-amber-500 bg-amber-50/60 dark:bg-amber-950/40 text-amber-900 dark:text-amber-200'
                    : 'border-surface-200 dark:border-surface-700 bg-surface-50 dark:bg-surface-800 text-surface-600 dark:text-surface-400'
                }`}
              >
                <Lock className={`w-4 h-4 shrink-0 ${isPrivate ? 'text-amber-500' : ''}`} />
                <div>
                  <div className="text-xs font-bold">Private Team</div>
                  <div className="text-[10px] opacity-80">
                    Invite-only (visible to members & Admins)
                  </div>
                </div>
              </button>
            </div>
          </div>

          {/* 6. Assign Team Lead(s) & 7. Add Team Members */}
          <div className="space-y-2.5">
            <div className="flex items-center justify-between">
              <label className="block text-xs font-bold uppercase tracking-wider text-surface-500">
                6 & 7. Assign Team Lead(s) & Team Members
              </label>
              <span className="text-[11px] text-surface-500">
                {leadUserIds.length} Lead(s) · {memberUserIds.length} Total Member(s)
              </span>
            </div>

            {!isAdmin && (
              <p className="text-[11px] text-amber-500 font-medium">
                Note: Only Workspace Admins can promote or demote Team Leads. You can add or manage regular team members below.
              </p>
            )}

            <div className="relative">
              <Search className="w-3.5 h-3.5 text-surface-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                placeholder="Search workspace users by name or email..."
                className="w-full pl-8 pr-3 py-1.5 text-xs bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-xl text-surface-900 dark:text-surface-100 placeholder-surface-400 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </div>

            <div className="max-h-44 overflow-y-auto rounded-xl border border-surface-200 dark:border-surface-700 bg-surface-50 dark:bg-surface-800/50 divide-y divide-surface-200 dark:divide-surface-700">
              {filteredUsers.map((u) => {
                const isLead = leadUserIds.includes(u.id);
                const isSelectedMember = memberUserIds.includes(u.id) || isLead;

                return (
                  <div
                    key={u.id}
                    className="px-3 py-2 flex items-center justify-between gap-2 hover:bg-white dark:hover:bg-surface-800 transition-colors"
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#0162fc] to-[#4b2efa] text-white flex items-center justify-center text-xs font-bold shrink-0">
                        {(u.fullName || 'U').charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-bold text-surface-900 dark:text-surface-100 truncate">
                          {u.fullName}
                        </p>
                        <p className="text-[10px] text-surface-500 truncate">
                          {u.title || u.role} · {u.email}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5 shrink-0">
                      {/* Assign Team Lead toggle (Admin only) */}
                      <button
                        type="button"
                        disabled={!isAdmin}
                        onClick={() => toggleLead(u.id)}
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold border transition-all ${
                          isLead
                            ? 'bg-amber-500/20 text-amber-600 dark:text-amber-300 border-amber-500/40'
                            : 'bg-white dark:bg-surface-800 text-surface-600 dark:text-surface-400 border-surface-200 dark:border-surface-700 hover:text-surface-900'
                        } ${!isAdmin ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                        title={
                          isAdmin
                            ? 'Toggle Team Lead role'
                            : 'Only Workspace Admins can assign Team Leads'
                        }
                      >
                        <Crown className="w-3 h-3" />
                        <span>Lead</span>
                      </button>

                      {/* Member inclusion toggle */}
                      <button
                        type="button"
                        onClick={() => toggleMember(u.id)}
                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-bold border transition-all cursor-pointer ${
                          isSelectedMember
                            ? 'bg-brand-600 text-white border-brand-600'
                            : 'bg-white dark:bg-surface-800 text-surface-600 dark:text-surface-400 border-surface-200 dark:border-surface-700 hover:text-surface-900'
                        }`}
                      >
                        {isSelectedMember ? (
                          <>
                            <Check className="w-3 h-3" />
                            <span>Added</span>
                          </>
                        ) : (
                          <span>+ Member</span>
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-surface-200 dark:border-surface-800">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl text-xs font-semibold text-surface-600 dark:text-surface-400 hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !name.trim()}
              className="flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-xs font-bold bg-brand-600 hover:bg-brand-700 text-white shadow-sm disabled:opacity-50 transition-all cursor-pointer"
            >
              <Sparkles className="w-3.5 h-3.5" />
              <span>
                {isSubmitting
                  ? isEditMode
                    ? 'Saving Changes...'
                    : 'Creating Team...'
                  : isEditMode
                  ? 'Save Team Changes'
                  : 'Create Team'}
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateSpaceModal;
